import { useCallback, useEffect, useState } from 'react';
import { getClinicalSummaryService } from '../../../services/clinicalSummary';
import { AllergyState, deriveAllergyState } from './allergyState';

export interface PatientAllergyState {
    /** False until the first read settles (success or failure). */
    loaded: boolean;
    /** Null until loaded; a failed read degrades to "not documented". */
    state: AllergyState | null;
    /** Re-read the summary, e.g. after an allergy update was saved. */
    reload: () => void;
}

/**
 * SHER-1057 — the patient's allergy state as a hook, so the allergy form can follow it
 * as well as the Clinical Summary banner. `parameter` keys the summary (survey-field type).
 * `enabled` false issues no read (a host that shows neither banner nor allergy field).
 */
export function usePatientAllergyState(
    uuidInvestigation: string,
    uuidPatient: string,
    parameter = 'allergy',
    enabled = true,
): PatientAllergyState {
    const [loaded, setLoaded] = useState(false);
    const [state, setState] = useState<AllergyState | null>(null);
    const [generation, setGeneration] = useState(0);

    useEffect(() => {
        let active = true;
        if (!enabled || !uuidInvestigation || !uuidPatient) return;
        getClinicalSummaryService(uuidInvestigation, uuidPatient)
            .then((res) => {
                if (!active) return;
                setState(deriveAllergyState(res.clinicalSummary?.[parameter]));
                setLoaded(true);
            })
            .catch(() => {
                // Never break the host page: degrade to "not documented".
                if (!active) return;
                setState(deriveAllergyState(null));
                setLoaded(true);
            });
        return () => { active = false; };
    }, [uuidInvestigation, uuidPatient, parameter, enabled, generation]);

    const reload = useCallback(() => setGeneration((g) => g + 1), []);
    return { loaded, state, reload };
}
